import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./InputSearchs.css";
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faChevronRight, faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import baseURL from '../url';
import moneda from '../moneda';

export default function InputSearchs() {
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredProducto, setFilteredProducto] = useState([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [selectedCategoria, setSelectedCategoria] = useState("Todos");
    const inputRef = useRef(null);

    useEffect(() => {
        cargarProductos();
    }, []);

    // Escuchar Escape y bloquear scroll del body
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape" && modalIsOpen) {
                closeModal();
            }
        };

        if (modalIsOpen) {
            document.body.style.overflow = "hidden";
            window.addEventListener("keydown", handleKeyDown);
            const timer = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
            return () => {
                clearTimeout(timer);
                window.removeEventListener("keydown", handleKeyDown);
            };
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            document.body.style.overflow = "unset";
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [modalIsOpen]);

    const cargarProductos = () => {
        fetch(`${baseURL}/productosGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                const prods = data.productos || [];
                setProductos(prods);
                // Extraer categorías únicas
                const cats = ["Todos", ...new Set(prods.map(p => p.categoria).filter(Boolean))];
                setCategorias(cats);
            })
            .catch(error => console.error('Error al cargar productos:', error));
    };

    const handleSearch = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        filtrar(term, selectedCategoria);
    };

    const handleCategoriaClick = (cat) => {
        setSelectedCategoria(cat);
        filtrar(searchTerm, cat);
    };

    const filtrar = (term, cat) => {
        let results = productos;
        if (cat && cat !== "Todos") {
            results = results.filter(p => p.categoria?.toLowerCase() === cat.toLowerCase());
        }
        if (term.trim() !== "") {
            const lower = term.toLowerCase();
            results = results.filter(p =>
                (p.titulo && p.titulo.toLowerCase().includes(lower)) ||
                (p.categoria && p.categoria.toLowerCase().includes(lower)) ||
                (p.descripcion && p.descripcion.toLowerCase().includes(lower))
            );
        }
        setFilteredProducto(results);
    };

    const openModal = () => {
        setModalIsOpen(true);
        setSearchTerm("");
        setSelectedCategoria("Todos");
        setFilteredProducto(productos);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setSearchTerm("");
    };

    const limpiarBusqueda = () => {
        setSearchTerm("");
        filtrar("", selectedCategoria);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const productosAMostrar = (searchTerm.trim() !== "" || selectedCategoria !== "Todos")
        ? filteredProducto
        : productos;

    const modalJSX = modalIsOpen && (
        <div className="searchModalOverlay" onClick={closeModal}>
            <div className="searchModalCard" onClick={(e) => e.stopPropagation()}>
                {/* Cabecera con barra de búsqueda y botón de cierre */}
                <div className="searchModalHeader">
                    <div className="searchModalInputWrapper">
                        <FontAwesomeIcon icon={faSearch} className="searchModalInputIcon" />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Buscar productos por nombre o categoría..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="searchModalInputField"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="searchModalClearButton"
                                onClick={limpiarBusqueda}
                                title="Limpiar búsqueda"
                                aria-label="Limpiar"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        className="searchModalCloseButton"
                        onClick={closeModal}
                        title="Cerrar búsqueda"
                        aria-label="Cerrar modal"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Filtros rápidos por categorías */}
                {categorias.length > 1 && (
                    <div className="searchModalCategories">
                        {categorias.map((cat) => (
                            <button
                                key={cat}
                                type="button"
                                className={`searchCategoryPill ${selectedCategoria === cat ? 'searchCategoryPillActive' : ''}`}
                                onClick={() => handleCategoriaClick(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                )}

                {/* Subtítulo / Contador de resultados */}
                <div className="searchModalInfoBar">
                    <span>
                        {searchTerm.trim() !== "" || selectedCategoria !== "Todos"
                            ? `${productosAMostrar.length} productos encontrados`
                            : `Productos sugeridos (${productosAMostrar.length})`}
                    </span>
                </div>

                {/* Lista de productos con scroll */}
                <div className="searchModalResultsList">
                    {productosAMostrar.length > 0 ? (
                        productosAMostrar.map((item) => (
                            <Link
                                key={item.idProducto}
                                to={`/producto/${item.idProducto}/${(item.titulo || '').replace(/\s+/g, '-')}`}
                                onClick={closeModal}
                                className="searchProductCardItem"
                            >
                                <div className="searchProductImgWrapper">
                                    <img
                                        src={item.imagen1 || '/logo.png'}
                                        alt={item.titulo}
                                        onError={(e) => { e.target.src = '/logo.png'; }}
                                    />
                                </div>
                                <div className="searchProductInfo">
                                    <h4 className="searchProductTitle">{item.titulo}</h4>
                                    <span className="searchProductCatBadge">{item.categoria}</span>
                                </div>
                                <div className="searchProductPriceWrapper">
                                    <strong className="searchProductPrice">
                                        {moneda} {Number(item.precio || 0).toFixed(2)}
                                    </strong>
                                    <FontAwesomeIcon icon={faChevronRight} className="searchProductArrow" />
                                </div>
                            </Link>
                        ))
                    ) : (
                        <div className="searchModalEmptyState">
                            <FontAwesomeIcon icon={faBoxOpen} className="searchEmptyIcon" />
                            <h4>No se encontraron productos</h4>
                            <p>No hay coincidencias para <strong>"{searchTerm}"</strong>. Intenta con otra palabra clave o selecciona una categoría.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="fondo-input">
            <div className="search-container">
                <button
                    type="button"
                    className="inputSerch"
                    onClick={openModal}
                    title="Buscar productos"
                    aria-label="Abrir buscador"
                >
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <strong>Buscar</strong>
                </button>

                {/* Portal que renderiza el modal directamente en el body */}
                {typeof document !== 'undefined' && ReactDOM.createPortal(modalJSX, document.body)}
            </div>
        </div>
    );
}
